import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money, dateEs } from "@/lib/formatters";
import { FileText, Users, TrendingUp, Clock } from "lucide-react";

export default function Dashboard() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const reqs = [];
        if (can("quotes_read")) reqs.push(api.get("/quotes")); else reqs.push(Promise.resolve({data: []}));
        if (can("clients_read")) reqs.push(api.get("/clients")); else reqs.push(Promise.resolve({data: []}));
        const [q, c] = await Promise.all(reqs);
        setQuotes(q?.data || []);
        setClients(c?.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [can]);

  const pending = quotes.filter((q) => ["borrador", "enviada"].includes(q.status));
  const accepted = quotes.filter((q) => q.status === "aceptada");
  const totalAccepted = accepted.reduce((s, q) => s + (q.total || 0), 0);
  const recentQuotes = [...quotes].slice(0, 6);

  const stats = [
    { label: "Cotizaciones", value: quotes.length, icon: FileText, color: "bg-[color:var(--pe-primary)]", testid: "stat-quotes" },
    { label: "Pendientes", value: pending.length, icon: Clock, color: "bg-amber-600", testid: "stat-pending" },
    { label: "Aceptadas", value: accepted.length, icon: TrendingUp, color: "bg-emerald-600", testid: "stat-accepted" },
    { label: "Clientes", value: clients.length, icon: Users, color: "bg-[color:var(--pe-accent)]", testid: "stat-clients" },
  ];

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="pe-label">PANEL DE CONTROL</div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mt-1">
        <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]">Hola, {user?.name?.split(" ")[0]}</h1>
        <div className="text-sm text-slate-500">{dateEs(new Date().toISOString())}</div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="pe-card p-5 flex items-start gap-4" data-testid={s.testid}>
              <div className={`${s.color} text-white p-3 rounded-md`}>
                <Icon size={20} />
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)] pe-numeric">
                  {loading ? "—" : s.value}
                </div>
                <div className="pe-label mt-1 opacity-70">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        <div className="pe-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-black text-[color:var(--pe-primary)]">Cotizaciones recientes</h2>
            <button onClick={() => navigate("/quotes")}
                    className="text-sm font-bold text-[color:var(--pe-accent)] hover:underline"
                    data-testid="link-see-all-quotes">Ver todas →</button>
          </div>
          {recentQuotes.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              Aún no hay cotizaciones. Empieza creando una nueva.
            </div>
          ) : (
            <div className="divide-y divide-[color:var(--pe-border)]">
              {recentQuotes.map((q) => (
                <div key={q.id}
                     className="py-3 flex items-center justify-between gap-4 hover:bg-slate-50 rounded-md px-2 cursor-pointer"
                     onClick={() => navigate(`/quotes/${q.id}`)}
                     data-testid={`recent-quote-${q.folio}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[color:var(--pe-primary)] pe-numeric">{q.folio}</div>
                    <div className="text-sm text-slate-600 truncate">{q.client?.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="pe-numeric font-bold">{money(q.total, q.currency)}</div>
                    <span className={`pe-badge pe-badge-${q.status}`}>{q.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pe-card p-6">
          <h2 className="text-xl font-black text-[color:var(--pe-primary)]">Resumen</h2>
          <div className="mt-6 space-y-4">
            <div>
              <div className="pe-label opacity-70">Valor aceptado</div>
              <div className="text-2xl font-black pe-numeric text-emerald-700 mt-1">{money(totalAccepted)}</div>
            </div>
            <div>
              <div className="pe-label opacity-70">Cotizaciones pendientes</div>
              <div className="text-2xl font-black pe-numeric text-amber-600 mt-1">{pending.length}</div>
            </div>
            <div>
              <div className="pe-label opacity-70">Rol de acceso</div>
              <div className="text-base font-bold text-slate-700 mt-1 uppercase tracking-wide">{user?.role}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
